import type { BabylonApp } from "../app/BabylonApp";
import {
  Color3,
  Color4,
  DirectionalLight,
  HemisphericLight,
  PhotoDome,
  Scene,
  ShadowGenerator,
  Vector3
} from "../app/babylon";
import { assets } from "./assets";
import { Slope } from "./world/Slope";
import { Trees } from "./world/Trees";
import { FinishLine } from "./world/FinishLine";
import { Rider, type RiderInputs } from "./actors/Rider";
import { ChaseCamera } from "./camera/ChaseCamera";
import { SnowSpray } from "./fx/SnowSpray";
import { Hud } from "./ui/Hud";
import {
  COUNTDOWN_DURATION,
  createGameState,
  startCountdown
} from "./state";

const START_Z = 4;

export async function createScene(app: BabylonApp): Promise<Scene> {
  const scene = new Scene(app.engine);
  scene.clearColor = new Color4(0.62, 0.78, 0.95, 1);
  scene.ambientColor = new Color3(0.5, 0.55, 0.65);

  const dome = new PhotoDome(
    "panorama",
    assets.panorama,
    { resolution: 64, size: 800 },
    scene
  );
  dome.mesh.isPickable = false;
  dome.mesh.applyFog = false;

  const hemi = new HemisphericLight("hemi", new Vector3(0.3, 1, 0.2), scene);
  hemi.intensity = 0.55;
  hemi.groundColor = new Color3(0.45, 0.5, 0.65);

  const sunDir = new Vector3(-0.5, -1, 0.3).normalize();
  const sun = new DirectionalLight("sun", sunDir, scene);
  sun.intensity = 1.9;
  sun.diffuse = new Color3(1, 0.96, 0.88);
  sun.autoUpdateExtends = false;
  sun.shadowFrustumSize = 90;
  sun.shadowMinZ = -80;
  sun.shadowMaxZ = 240;

  const shadowGen = new ShadowGenerator(1024, sun);
  shadowGen.usePercentageCloserFiltering = true;
  shadowGen.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
  shadowGen.bias = 0.002;
  shadowGen.normalBias = 0.015;
  shadowGen.darkness = 0;

  const slope = new Slope(scene);
  slope.groomed.receiveShadows = true;
  slope.leftPowder.receiveShadows = true;
  slope.rightPowder.receiveShadows = true;

  const rider = new Rider(scene);
  await rider.load();

  for (const mesh of rider.shadowMeshes()) {
    shadowGen.addShadowCaster(mesh, true);
  }

  const trees = new Trees(scene);
  await trees.load(slope);

  for (const tmpl of trees.templates) {
    tmpl.isVisible = true;
    tmpl.setEnabled(true);
    tmpl.position.set(0, -10000, 0);
    shadowGen.addShadowCaster(tmpl, true);
  }
  const finishZ = slope.length - 40;
  const finish = new FinishLine(scene, slope, finishZ);
  for (const mesh of finish.root.getChildMeshes(false)) {
    shadowGen.addShadowCaster(mesh, true);
  }

  rider.resetTo(START_Z, slope);

  const chase = new ChaseCamera(scene);
  chase.warpTo(rider.position, rider.heading);
  scene.activeCamera = chase.camera;

  const spray = new SnowSpray(scene);

  const params = new URLSearchParams(window.location.search);
  const autoplay =
    params.has("autoplay") &&
    params.get("autoplay") !== "0" &&
    params.get("autoplay") !== "false";

  const isTouchDevice =
    typeof window !== "undefined" &&
    (navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);

  const hud = new Hud(isTouchDevice);
  const game = createGameState();

  const keys = new Set<string>();
  const touchState = { left: false, right: false };

  const restart = (): void => {
    rider.resetTo(START_Z, slope);
    chase.warpTo(rider.position, rider.heading);
    startCountdown(game);
    presentationStart = performance.now();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    keys.add(e.code);
    if (e.code === "KeyR") {
      restart();
    }
  };
  const onKeyUp = (e: KeyboardEvent) => keys.delete(e.code);
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);

  const canvas = scene.getEngine().getRenderingCanvas();
  const updateTouches = (event: TouchEvent): void => {
    touchState.left = false;
    touchState.right = false;
    const width = window.innerWidth;
    for (let i = 0; i < event.touches.length; i += 1) {
      const t = event.touches[i];
      if (t.clientX < width / 2) touchState.left = true;
      else touchState.right = true;
    }
  };
  const onTouchStart = (event: TouchEvent): void => {
    event.preventDefault();
    if (game.phase === "finished") {
      restart();
      return;
    }
    updateTouches(event);
  };
  const onTouchMove = (event: TouchEvent): void => {
    event.preventDefault();
    updateTouches(event);
  };
  const onTouchEnd = (event: TouchEvent): void => {
    event.preventDefault();
    updateTouches(event);
  };

  if (canvas && isTouchDevice) {
    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd, { passive: false });
    canvas.addEventListener("touchcancel", onTouchEnd, { passive: false });
  }

  const inputIsLeft = (): boolean =>
    keys.has("ArrowLeft") || keys.has("KeyA") || touchState.left;
  const inputIsRight = (): boolean =>
    keys.has("ArrowRight") || keys.has("KeyD") || touchState.right;

  const keyboardDriver = (): RiderInputs => {
    if (inputIsLeft()) return { left: true, right: false };
    if (inputIsRight()) return { left: false, right: true };
    return { left: false, right: false };
  };

  let presentationStart = performance.now();
  const presentationDriver = (): RiderInputs => {
    if (inputIsLeft()) return { left: true, right: false };
    if (inputIsRight()) return { left: false, right: true };

    const lookahead = 14;
    const ahead = slope.sampleAtZ(rider.position.z + lookahead);

    const t = (performance.now() - presentationStart) / 1000;
    const warmup = Math.min(1, t / 2);
    const wobble = warmup * 0.15 * Math.sin(t * 0.35);
    let desiredX = ahead.centerX + wobble;

    const projectedX = rider.position.x + rider.velocity.x * 0.6;
    const dodgeRange = 36;
    for (const tree of trees.placements) {
      const dz = tree.position.z - rider.position.z;
      if (dz < 1 || dz > dodgeRange) continue;
      const dxFromDesired = tree.position.x - desiredX;
      const dxFromProj = tree.position.x - projectedX;
      const dx =
        Math.abs(dxFromDesired) < Math.abs(dxFromProj) ? dxFromDesired : dxFromProj;
      if (Math.abs(dx) > 5) continue;
      const push = dx >= 0 ? -5 : 5;
      const aheadFactor = 1 - dz / dodgeRange;
      desiredX += push * aheadFactor;
    }

    desiredX = Math.max(
      ahead.centerX - 2.5,
      Math.min(ahead.centerX + 2.5, desiredX)
    );

    const signal = (rider.position.x - desiredX) + rider.velocity.x * 1.4;
    const deadZone = 0.2;
    const proportional = 6.0;
    let leanTarget = 0;
    if (Math.abs(signal) > deadZone) {
      const adjusted =
        -Math.sign(signal) * (Math.abs(signal) - deadZone) / proportional;
      leanTarget = Math.max(-1, Math.min(1, adjusted));
    }
    return { left: false, right: false, leanTarget };
  };

  const restartLabel = isTouchDevice ? "Tap to restart" : "Press R to restart";

  let firstFrameSeen = false;
  scene.onBeforeRenderObservable.add(() => {
    const rawDt = scene.getEngine().getDeltaTime() / 1000;
    const dt = Math.min(rawDt, 1 / 30);
    const now = performance.now();

    if (!firstFrameSeen) {
      firstFrameSeen = true;
      game.countdownStart = now;
    }

    finish.update();

    if (game.phase === "countdown") {
      const elapsed = (now - game.countdownStart) / 1000;
      if (elapsed >= COUNTDOWN_DURATION) {
        game.phase = "running";
        game.runStart = now;
        presentationStart = now;
        hud.showTimer(0);
      } else {
        hud.showCountdown(elapsed);
      }
      sun.position.set(
        rider.position.x - sunDir.x * 50,
        rider.position.y - sunDir.y * 50,
        rider.position.z - sunDir.z * 50
      );
      dome.mesh.position.x = rider.position.x;
      dome.mesh.position.z = rider.position.z;
      chase.update(dt, rider.position, rider.heading);
      return;
    }

    if (game.phase === "running") {
      const inputs = autoplay ? presentationDriver() : keyboardDriver();
      rider.step(dt, slope, inputs);

      if (!rider.crashed && trees.hits(rider.position, 0.45)) {
        rider.triggerCrash();
        hud.showCrash();
      }

      if (rider.crashed && rider.crashTimer > 2.5) {
        rider.resetTo(START_Z, slope);
        chase.warpTo(rider.position, rider.heading);
        presentationStart = now;
      }

      if (!rider.crashed) {
        hud.showTimer((now - game.runStart) / 1000);
      }

      if (!rider.crashed && finish.hasCrossed(rider.position)) {
        game.phase = "finished";
        game.finishTime = (now - game.runStart) / 1000;
        hud.showFinish(game.finishTime, restartLabel);
        rider.velocity.set(0, 0, 0);
      }
    } else if (game.phase === "finished") {
      rider.velocity.set(0, 0, 0);
    }

    sun.position.set(
      rider.position.x - sunDir.x * 50,
      rider.position.y - sunDir.y * 50,
      rider.position.z - sunDir.z * 50
    );

    dome.mesh.position.x = rider.position.x;
    dome.mesh.position.z = rider.position.z;

    chase.update(dt, rider.position, rider.heading);

    const fx = Math.sin(rider.heading);
    const fz = Math.cos(rider.heading);
    const right = new Vector3(fz, 0, -fx);
    if (rider.lean < 0) right.scaleInPlace(-1);
    const back = new Vector3(-fx, 0, -fz);
    spray.update(rider.carveIntensity(), rider.position, right, back, rider.outsideEdgeOffset());
  });

  await scene.whenReadyAsync();
  for (let i = 0; i < 4; i += 1) {
    scene.render();
  }

  scene.onDisposeObservable.add(() => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
    if (canvas && isTouchDevice) {
      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);
      canvas.removeEventListener("touchcancel", onTouchEnd);
    }
    hud.dispose();
  });

  return scene;
}
